<template>
  <div class="min-h-screen flex items-center justify-center relative overflow-hidden">
    <!-- --------------------------------------------------------------->
    <!-- 🌐 Login Page -->
    <!-- --------------------------------------------------------------->
    <div class="glass-panel w-full max-w-md p-8 relative z-10 mx-4">
      <!-- 🌐 Logo -->
      <div class="flex flex-col items-center mb-8">
        <img src="~assets/logos/pulsarLogoDark.svg" class="w-32 h-32 mb-2" alt="PulsarAI Logo" />
        <span class="text-4xl font-bold tracking-tight text-gray-900 dark:text-white whitespace-nowrap">{{ applicationOfficialName }}</span>
      </div>

      <!-- --------------------------------------------------------------->
      <!-- 🌐 Account Lookup Form -->
      <!-- --------------------------------------------------------------->
      <template v-if="!account && isHydrated">
        <div class="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div>
            <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Let's Find Your {{ applicationName }} Account
            </h4>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">Enter your email address to see available login options.</p>
          </div>

          <form @submit.prevent="accountLoginOptions" class="space-y-4">
            <div class="relative group">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 group-focus-within:text-purple-500 transition-colors">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </span>
              <input
                v-model="email"
                type="email"
                placeholder="email@example.com"
                class="glass-input w-full pl-10"
                required
                autofocus
              />
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="w-full glass-button bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 flex items-center justify-center space-x-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <span v-if="!loading">Get Account</span>
              <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </button>
          </form>
          
          <div class="mt-6 text-center text-sm">
            <span class="text-gray-400">Don't have an account? </span>
            <NuxtLink to="/register" class="text-purple-400 hover:text-purple-300 font-semibold transition-colors">Start your free trial</NuxtLink>
          </div>
        </div>
      </template>

      <!-- --------------------------------------------------------------->
      <!-- 🌐 Login Method Selection -->
      <!-- --------------------------------------------------------------->
      <template v-if="account && isHydrated">
        <div class="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div class="text-center">
            <h4 class="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Select a login method for:</h4>
            <div class="flex items-center justify-center space-x-2 mt-2">
              <span class="text-lg font-bold text-gray-900 dark:text-white">{{ email }}</span>
              <button @click="resetAccount" class="text-gray-400 hover:text-red-500 transition-colors">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </button>
            </div>
          </div>

          <div class="space-y-3">
            <button
              v-for="item in loginSelectionOptions()"
              :key="item.value"
              @click="federationLoginSelection(item.value)"
              class="w-full glass-panel-sm p-4 flex items-center space-x-4 hover:bg-white/10 transition-all border border-white/5 hover:border-purple-500/50 group"
            >
              <div class="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                <Icon v-if="item.icon" :name="item.icon as string" class="w-8 h-8" mode="svg" />
              </div>
              <div class="text-left">
                <div class="text-sm font-bold text-gray-900 dark:text-white tracking-wide group-hover:text-purple-400 transition-colors">{{ item.title }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">{{ item.desc }}</div>
              </div>
            </button>
          </div>

          <!-- Divider with "or" -->
          <div v-if="loginSelectionOptions().length > 0 && ifPasswordLogin" class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-gray-700/50"></div>
            </div>
            <div class="relative flex justify-center text-xs border-b uppercase">
              <span class="px-2 bg-transparent border-gray-700/50 font-medium text-white">or continue with password</span>
            </div>
          </div>

          <!-- 🌐 Username/Password Login -->
          <form v-if="ifPasswordLogin" @submit.prevent="usernamePasswordLogin(email, password)" class="space-y-4">
            <div class="space-y-1">
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider mb-1">Password</label>
              <div class="relative group">
                <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 group-focus-within:text-purple-500 transition-colors">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                </span>
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="••••••••"
                  class="glass-input w-full pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-purple-400 transition-colors"
                >
                  <svg v-if="!showPassword" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  <svg v-else class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.882 9.882L5.146 5.147m13.707 13.707l-2.793-2.793M22.354 22.354l-21.21-21.21" /></svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              :disabled="loading || !isLoginFormValid"
              class="w-full glass-button bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 flex items-center justify-center space-x-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <span v-if="!loading">Sign In</span>
              <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </button>
          </form>
        </div>
      </template>

      <!-- 🌐 Loading State -->
      <template v-if="!isHydrated">
        <div class="flex justify-center items-center py-12">
          <div class="relative w-16 h-16">
            <div class="absolute inset-0 border-4 border-purple-500/20 rounded-full"></div>
            <div class="absolute inset-0 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </template>

      <!-- 🌐 Error/Warning Messages -->
      <div v-if="warningMessage" class="mt-6 animate-in fade-in zoom-in duration-300">
        <div class="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl flex items-center space-x-3">
          <svg class="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span class="text-sm font-medium">{{ warningMessage }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { userLogin } from '@/scripts/auth/applicationAuth'
import { timestampAdd } from 'firebase/firestore/pipelines'

// Define types
type AllowedLoginMethods = 'snowflakeOauth' | 'snowflakeRSA' | 'snowflakeNoAuth' | 'applicationLogin'

// 💬 Page Layout
// /////////////////////////////////
definePageMeta({
  unauthenticatedOnly: true,
})

const test = () => {
  alert('This is working!')
  console.log('This is working!')
}

// 💬 Store Variables
// /////////////////////////////////
const { applicationName, applicationOfficialName } = storeToRefs(useGlobalsStore())

// 💬 Global Variables
// /////////////////////////////////
const router = useRouter()
const account = ref<any>('')
const warningMessage = ref<string | null>(null)
const loading = ref(false)
const loginMethods = ref<AllowedLoginMethods[]>([])

// 💬 Form Fields
// /////////////////////////////////
const email = ref<string>('')
const password = ref<string>('')
const showPassword = ref(false)

// 💬 Client-side hydration state
// /////////////////////////////////
const isHydrated = ref(false)

// 💬 Get Account Login Options
// /////////////////////////////////
async function accountLoginOptions() {
  try {

    // Basic email validation
    if (!email.value || !/.+@.+\..+/.test(email.value)) {
      warningMessage.value = 'Please enter a valid email address'
      return
    }

    loading.value = true
    warningMessage.value = null
    const response: any = await useCloudFunction('app_account_login_options', { email: email.value })

    if (!response || response.error || JSON.stringify(response) === '{}') {
      if (response.error?.message === 'internal') warningMessage.value = 'Could Not Connect To Server'
      else warningMessage.value = 'No Account Found For This Email'
      loading.value = false

      return
    }

    // Clear Previous Login Methods
    loginMethods.value = []
    
    for (const [key, value] of Object.entries(response)) {
      if (key === 'account') {
        account.value = value
      } else {
        loginMethods.value.push(key as AllowedLoginMethods)
        if (key === 'snowflakeOauth') snowflakeAccountSettings.value = value
      }
    }

    // Set snowflakeOauth to default if exists - otherwise disable snowflake and select applicationLogin
    if (loginMethods.value.includes('snowflakeOauth')) loginSelection.value = 'snowflakeOauth'
    else loginSelection.value = 'applicationLogin'
    loading.value = false
  } catch (error) {
    loading.value = false
    console.error('Error Getting Account Login Options', error)
    warningMessage.value = 'Error Getting Account Login Options'
  }
}

const loginSelectionOptions = () => {
  const loginMethodsList = []
  if (loginMethods.value.includes('snowflakeOauth')) {
    loginMethodsList.push({ 
      disabled: false, 
      icon: 'custom:logos-snowflake-text-square', 
      title: 'SNOWFLAKE OAUTH LOGIN', 
      desc: `Login to ${applicationName.value} using your Snowflake credentials`, 
      value: 'snowflakeOauth', 
      action: '' 
    })
    // loginMethodsList.push({ 
    //   disabled: false, 
    //   icon: 'custom:logos-databricks-text-square', 
    //   title: 'DATABRICKS OAUTH LOGIN', 
    //   desc: `Login to ${applicationName.value} using your Databricks credentials`, 
    //   value: 'databricksOauth' 
    // })
  }
  return loginMethodsList
}
const loginSelection = ref<string>('')
const snowflakeAccountSettings = ref<any>({})
const ifPasswordLogin = computed(() => (loginMethods.value.includes('snowflakeRSA') || loginMethods.value.includes('snowflakeNoAuth')))


// 💬 Sets The Default Login Method
// /////////////////////////////////
function setDefaultLogin(selection: string) {
  if (import.meta.client) { // Only run on client side
    useSetLocalStorage('defaultLogin', JSON.stringify({ 
      account: account.value, 
      loginSelection: selection, 
      email: email.value, 
      loginMethods: loginMethods.value, 
      snowflakeAccountSettings: snowflakeAccountSettings.value 
    }))
  }
}


// 💬 Check if login form is valid
// /////////////////////////////////
const isLoginFormValid = computed(() => {
  const emailValid = !!email.value && /.+@.+\..+/.test(email.value)
  const passwordValid = !!password.value && password.value.length >= 6
  return emailValid && passwordValid
})

const selectLoginMethod = (method: string) => {
  loginSelection.value = method
  warningMessage.value = null
}

const resetAccount = () => {
  account.value = ''
  warningMessage.value = null
  loginMethods.value = []
  loginSelection.value = ''
}

// //////////////////////////////////////////////////////////////////
// 💬 Login Functions
// //////////////////////////////////////////////////////////////////
function federationLoginSelection(selectionName: string) {
  if (selectionName === 'snowflakeOauth') snowflakeOauthLogin()
}

// 💬 Oauth Login
// //////////////////////////////////////////////////////////////////
async function snowflakeOauthLogin() {
  loading.value = true
  warningMessage.value = null
  setDefaultLogin('snowflakeOauth')

  const { sfId, clientId, userId } = snowflakeAccountSettings.value

  // Get Code and Tokens
  const { snowflakeOauth } = useSnowflakeAuthStore()
  const response: any = await snowflakeOauth(sfId, clientId, userId)

  // If Login Response Failed
  if (response.error)
    warningMessage.value = response.error

  // If No Login Token Was Received
  else if (!response.loginToken)
    warningMessage.value = 'Something went wrong and we could not log you in.'

  // Otherwise Logging User In
  else
    await userLogin(email.value, null, response.loginToken, true)

  loading.value = false
}


// 💬 Username / Password Login
// //////////////////////////////////////////////////////////////////
const usernamePasswordLogin = async (username: string, userpassword: string) => {
  loading.value = true
  warningMessage.value = null
  setDefaultLogin('applicationLogin')

  const loginResponse = await userLogin(username, userpassword, null)

  // If Login Failed
  if (loginResponse.error) {
    loading.value = false

    // If error starts with "Failed" the show "Could not log you in."
    if (loginResponse.error.startsWith('Failed'))
      warningMessage.value = 'Login was not successful. Check your credentials and try again.'
    else
      warningMessage.value = loginResponse.error
  }
  loading.value = false
}

// //////////////////////////////////////////////////////////////////
// 💬 Getting Saved Login Options From Local Storage
// //////////////////////////////////////////////////////////////////

// Load saved login data (simulate localStorage)
onMounted(async () => {
  // Mark as hydrated to prevent SSR/client mismatches
  isHydrated.value = true

  // If in local development mode - Bypass Authentication
  if (import.meta.dev) {
    try {
      console.log('Local development mode - Bypassing Authentication using .env credentials...')
      const devConfig: any = await $fetch('/api/dev-login')
      if (devConfig && devConfig.email && devConfig.password) {
        email.value = devConfig.email
        password.value = devConfig.password
        await usernamePasswordLogin(email.value, password.value)
      } else {
         console.warn('.env credentials not found. Defaulting to standard login.')
      }
    } catch (e) {
      console.warn('Dev auto-login failed:', e)
    }
  }
  
  // Check if account login options are in localStorage
  // Only run on client side to prevent hydration errors
  if (import.meta.client) {
    try {
      const defaultLogin = useGetLocalStorage('defaultLogin')
      if (defaultLogin) {
        const loginOptions = JSON.parse(defaultLogin)

        account.value = loginOptions.account || ''
        loginSelection.value = loginOptions.loginSelection || ''
        email.value = loginOptions.email || ''
        loginMethods.value = loginOptions.loginMethods || []
        snowflakeAccountSettings.value = loginOptions.snowflakeAccountSettings || {}
      }
    } catch (error) {
      console.error('Error parsing saved login data:', error)
      // Clear corrupted data
      if (import.meta.client) {
        localStorage.removeItem('defaultLogin')
      }
    }
  }
})
</script>
